import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Users, ArrowRight, Archive, ChevronDown } from 'lucide-react';
import { trainings } from '../data/trainings';

// Filter only archived trainings
const archivedTrainings = trainings.filter(t => t.status === 'archived');

const TrainingsArchivePage = () => {
  const [expandedEvents, setExpandedEvents] = useState({});

  const toggleEvent = (eventName) => {
    setExpandedEvents(prev => ({
      ...prev,
      [eventName]: !prev[eventName]
    }));
  };

  // Group by eventName and preserve insertion order
  const groupedByEvent = archivedTrainings.reduce((acc, t) => {
    if (!acc[t.eventName]) acc[t.eventName] = [];
    acc[t.eventName].push(t);
    return acc;
  }, {});

  // Get unique event names in reverse order (most recent first)
  const eventNames = Object.keys(groupedByEvent).reverse();

  return (
    <div className="min-h-screen pt-20" data-testid="trainings-archive-page">
      {/* Hero */}
      <section className="py-12 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="hero-glow top-0 left-1/4" />
        <div className="container-main relative z-10">
          <div className="max-w-3xl">
            <Link to="/services/quarterly-trainings" className="inline-flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--primary)] mb-6 font-mono text-sm">
              <ArrowRight className="w-4 h-4 rotate-180" /> Back to Upcoming Trainings
            </Link>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 border border-[var(--primary)] bg-[var(--primary)]/10 flex items-center justify-center">
                <Archive className="w-8 h-8 text-[var(--primary)]" />
              </div>
              <div>
                <p className="font-mono text-sm uppercase tracking-wider text-[var(--primary)]">Past Events</p>
                <h1 className="text-3xl md:text-5xl font-bold text-[var(--text-main)]">Training Archive</h1>
              </div>
            </div>
            <p className="text-[var(--text-muted)] text-lg md:text-xl leading-relaxed">
              Browse our past training events.
            </p>
          </div>
        </div>
      </section>

      {/* Archive List */}
      <section className="py-16">
        <div className="container-main">
          {eventNames.length > 0 ? (
            <div className="space-y-12">
              {eventNames.map((eventName) => {
                const eventTrainings = groupedByEvent[eventName];
                const dateRange = eventTrainings[0]?.date;
                return (
                  <div key={eventName}>
                    {/* Event Header */}
                    <button
                      onClick={() => toggleEvent(eventName)}
                      className="w-full text-left mb-8 pb-6 border-b border-[var(--border)] hover:bg-[var(--surface)]/50 transition-colors duration-200 p-2 -m-2"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <span className="px-3 py-1 bg-[var(--primary)]/20 text-[var(--primary)] text-xs font-mono uppercase">Past Event</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <h2 className="text-2xl md:text-3xl font-bold text-[var(--text-main)] mb-2">{eventName}</h2>
                          <p className="text-[var(--text-muted)] font-mono text-sm">{dateRange} · {eventTrainings.length} Training{eventTrainings.length !== 1 ? 's' : ''}</p>
                        </div>
                        <ChevronDown
                          className={`w-6 h-6 text-[var(--primary)] transition-transform duration-300 flex-shrink-0 ${expandedEvents[eventName] ? 'rotate-180' : ''}`}
                        />
                      </div>
                    </button>

                    {/* Training Cards Grid */}
                    {expandedEvents[eventName] && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                      {eventTrainings.map((training) => (
                        <div key={training.id} className="bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--primary)] transition-colors duration-300 overflow-hidden" data-testid={`archived-training-card-${training.id}`}>
                          <div className="p-6 border-b border-[var(--border)]">
                            <div className="flex items-center gap-2 mb-3">
                              <span className="px-2 py-1 bg-[var(--primary)]/20 text-[var(--primary)] text-xs font-mono uppercase">Completed</span>
                              <span className={`px-2 py-1 text-xs font-mono uppercase ${
                                training.level === 'Beginner' ? 'bg-[var(--primary)]/20 text-[var(--primary)]' :
                                training.level === 'Intermediate' ? 'bg-[var(--accent-cyan)]/20 text-[var(--accent-cyan)]' :
                                training.level === 'Advanced' ? 'bg-[var(--accent-purple)]/20 text-[var(--accent-purple)]' :
                                'bg-[var(--error)]/20 text-[var(--error)]'
                              }`}>{training.level}</span>
                            </div>
                            <h3 className="text-xl font-bold text-[var(--text-main)] mb-2">{training.title}</h3>
                            <p className="text-[var(--text-muted)] text-sm leading-relaxed">{training.description}</p>
                          </div>
                          <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div className="flex items-center gap-3"><Calendar className="w-5 h-5 text-[var(--primary)]" /><div><p className="text-xs text-[var(--text-muted)]">Date</p><p className="text-sm text-[var(--text-main)] font-mono">{training.date}</p></div></div>
                              <div className="flex items-center gap-3"><Clock className="w-5 h-5 text-[var(--primary)]" /><div><p className="text-xs text-[var(--text-muted)]">Duration</p><p className="text-sm text-[var(--text-main)] font-mono">{training.duration}</p></div></div>
                            </div>
                            <div>
                              <p className="text-xs text-[var(--text-muted)] mb-2">Instructor{training.instructors.length > 1 ? 's' : ''}</p>
                              <div className="flex flex-wrap gap-4">
                                {training.instructors.map((instructor, idx) => (
                                  <div key={idx} className="flex items-center gap-2">
                                    {instructor.photo ? (
                                      <img
                                        src={instructor.photo}
                                        alt={instructor.name}
                                        title={instructor.name}
                                        className="w-10 h-10 rounded-full object-cover border-2 border-[var(--primary)] flex-shrink-0"
                                        loading="lazy"
                                        decoding="async"
                                      />
                                    ) : (
                                      <div
                                        title={instructor.name}
                                        className="w-10 h-10 rounded-full bg-[var(--surface-highlight)] border-2 border-[var(--primary)] flex items-center justify-center font-mono text-[var(--primary)] text-xs font-bold flex-shrink-0"
                                      >
                                        {instructor.name.split(' ').map(n => n[0]).join('')}
                                      </div>
                                    )}
                                    <span className="text-sm text-[var(--text-main)] font-mono">{instructor.name}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {training.tags.map((tag, idx) => (<span key={idx} className="px-2 py-1 bg-[var(--surface-highlight)] border border-[var(--border)] text-xs text-[var(--text-muted)] font-mono">{tag}</span>))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <Archive className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
              <p className="text-[var(--text-muted)] text-lg">No archived trainings found.</p>
              <Link to="/services/quarterly-trainings" className="mt-4 inline-block text-[var(--primary)] font-mono hover:underline">View Upcoming Trainings</Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default TrainingsArchivePage;
